import { DataTable, sparkToast } from '@spark';
import './DatasetTable.css';
import { FullTextModal, getDatasetColumns, updateRecordResponse } from '@/features/datasets/components/DataSelection/ColumnsAndData';
import { useState, useEffect, useMemo, useCallback, useContext, useRef } from 'react';
import { data_from_server_post } from '@/shared/http/client';
import { TaskType } from '@/shared/domain/taskTypes';
import ImprovementMode from '@/features/datasets/components/DataSelection/ImprovementMode';
import { ExtraParamsColumnManager } from '@/features/datasets/components/DataSelection/ExtraParamsColumnManager';
import { extractExtraParamsFromPrompt } from '@/features/datasets/components/DataSelection/ExtraParamsUtils.js';
import { RequestExampleSearchInput } from '@/features/datasets/components/DataSelection/RequestExampleSearchInput';
import { useAuth } from '@/features/auth/AuthContext';
import { BulkActionsBar } from '@/features/datasets/components/DataSelection/BulkActionsBar.jsx';
import { ReplaceLabelModal } from '@/features/datasets/components/DataSelection/ReplaceLabelModal.jsx';
import { ExportButton } from '@/features/datasets/components/DataSelection/ExportButton.jsx';
import { resolveDatasetNameForRecord } from '@/features/datasets/utils/datasetCloneNames';
import { formatFiltersForAPI } from '@/features/datasets/utils/formatFiltersForAPI';
import { useDatasetCardFilterValues } from '@/features/datasets/hooks/useDatasetCardFilterValues';
import { useToolCallFilterValues } from '@/features/datasets/hooks/useToolCallFilterValues';
import { isAgenticAutonomyLevel } from '@/features/tasks/components/CreationWizard/businessGoals/autonomyLevels';
import { improvementModeFilterToTableFilters } from '@/features/datasets/utils/improvementModeTableFilters';
import { filterColumnsForImproveModal } from '@/features/datasets/utils/improveModalColumns';
import { GlobalContext } from '@/app/providers/globalState';
export const DatasetTable = ({
    dataset,
    isLoading,
    handleTableChange,
    pageSize: pageSizeProp,
    title,
    setIsInDB,
    currentUsecase,
    isBenchmark,
    metadata,
    availableExtraParamsKeys = [],
    crossReferenceFields = {},
    description,
    labelsUpdated,
    onOpenLabelModal,
    currentFilters,
    activeSorter,
    setCurrentFilters,
    setActiveSorter,
    datasetName,
    allLabelOptions,
    refreshCategories,
    relationshipTypes,
    refreshRelationshipTypes,
    updateRecord,
    isArchived = false,
    refreshRecordCounts,
    onDataOverviewRefresh,
    onRetrainRequest,
    columnPreset,
    improvementModeProps = {},
    onNestedOverlayChange,
    onValidationProgressChange,
    onTablePopoverChange,
}) => {
    // Track the current page 
    const [currentPage, setCurrentPage] = useState(1);
    // Track the page size locally so the size changer ("X / page") actually
    // applies and refetches. Seed from the prop, which serves as the default.
    const [pageSize, setPageSize] = useState(pageSizeProp);
    // Suppress loading after first successful load to avoid flicker on periodic refetches
    const [hasSeenLoading, setHasSeenLoading] = useState(false);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    // Replace label modal state
    const [isReplaceLabelModalVisible, setIsReplaceLabelModalVisible] = useState(false);
    const { role } = useAuth();
    const { generationProgress, updateUsecase, refreshUsecases } = useContext(GlobalContext);
    // Remember last seen generation status so we refetch once per completion.
    const lastGenerationStatusRef = useRef(null);
    // Track active filters
    const [activeFilters, setActiveFilters] = useState(0);
    // Track display names mapping
    const [displayNames, setDisplayNames] = useState({});
    // Track when display names are refreshed for visual feedback
    const [displayNamesRefreshed, setDisplayNamesRefreshed] = useState(false);

    // Track if validation counts are loading
    const [isValidationCountsLoading, setIsValidationCountsLoading] = useState(false);
    // Track whether validation counts have finished loading at least once, so
    // background refreshes (e.g. toggling a row's review checkbox) update the
    // cards quietly instead of flashing the full-section spinner.
    const [hasLoadedValidationCountsOnce, setHasLoadedValidationCountsOnce] = useState(false);

    // Track extra parameters titles for dynamic columns
    const promptParams = Object.keys(extractExtraParamsFromPrompt(currentUsecase?.prompt));
    const [extraParamsTitles, setExtraParamsTitles] = useState(
        promptParams.filter((item) => (availableExtraParamsKeys || []).includes(item))
    );

    // When detected keys arrive (from metadata), reconcile selected columns.
    useEffect(() => {
        const detected = Array.isArray(availableExtraParamsKeys) ? availableExtraParamsKeys : [];
        if (detected.length === 0) return;

        const detectedSet = new Set(detected);
        const special = new Set(['optune_score', 'data_creation_type', 'dataset']);

        setExtraParamsTitles((prev) => {
            const prevArr = Array.isArray(prev) ? prev : [];
            // Keep only valid keys (detected or special)
            const kept = prevArr.filter((k) => detectedSet.has(k) || special.has(k));
            // If user hasn't selected any detected columns yet, default to prompt params that exist
            const hasAnyDetected = kept.some((k) => detectedSet.has(k));
            if (!hasAnyDetected) {
                const promptIntersect = promptParams.filter((k) => detectedSet.has(k));
                return Array.from(new Set([...kept, ...promptIntersect]));
            }
            return kept;
        });
    }, [availableExtraParamsKeys, currentUsecase?.prompt]);

    // Distinct dataset_card values for its checkbox column filter (fetched
    // lazily, only while the column is visible).
    const datasetCardFilterValues = useDatasetCardFilterValues(
        currentUsecase,
        isBenchmark,
        (extraParamsTitles || []).includes('dataset_card')
    );

    // Distinct tool names for the agentic TOOLS CALLED checkbox column filter
    // (fetched lazily, only when the task is agentic and the column renders).
    const toolCallFilterValues = useToolCallFilterValues(
        currentUsecase,
        isBenchmark,
        isAgenticAutonomyLevel(currentUsecase?.autonomy_level)
    );

    // Track validation progress
    const [validationProgress, setValidationProgress] = useState({});
    const [entityCounts, setEntityCounts] = useState({});
    const [relationCounts, setRelationCounts] = useState({});
    // `{ total, reviewed }` for generative tasks (null otherwise).
    const [recordCounts, setRecordCounts] = useState(null);
    const [rankScoreInitialized, setRankScoreInitialized] = useState(false);
    // Occurrence counts for the special "Unlabeled" ([]/missing) and
    // "None Selected" ([""]) filter buckets. Only shown when > 0.
    const [unlabeledCount, setUnlabeledCount] = useState(0);
    const [noneSelectedCount, setNoneSelectedCount] = useState(0);
    const [focusIncompleteToken, setFocusIncompleteToken] = useState(0);
    const {
        focusIncompleteToken: parentFocusIncompleteToken = 0,
        ...restImprovementModeProps
    } = improvementModeProps;
    const tablePopoverIdsRef = useRef(new Set());
    const handleTablePopoverChange = useCallback((id, isOpen) => {
        if (!id) return;
        if (isOpen) {
            tablePopoverIdsRef.current.add(id);
        } else {
            tablePopoverIdsRef.current.delete(id);
        }
        onTablePopoverChange?.(tablePopoverIdsRef.current.size > 0);
    }, [onTablePopoverChange]);

    // Track search state
    const [searchQuery, setSearchQuery] = useState('');
    // Track selected records for bulk actions
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    // Track if "Select All" is active (optimistic selection mode)
    const [isSelectAllActive, setIsSelectAllActive] = useState(false);
    // Track IDs that are excluded when Select All is active (manually deselected records)
    const [excludedIds, setExcludedIds] = useState([]);
    // Track if bulk operations are in progress
    const [isBulkOperationInProgress, setIsBulkOperationInProgress] = useState(false);
    // Calculate actual page count based on current dataset
    const actualTotalPages = useMemo(() => {
        if (!dataset || dataset.length === 0) return 1;

        // If we have filters applied, calculate the accurate page count
        // based on the actual visible data
        if (activeFilters > 0 || activeSorter) {
            // The total shown in metadata might be an estimate
            // Use the actual dataset length for current page
            const totalRecords = metadata.num_records || 0;
            return Math.max(1, Math.ceil(totalRecords / pageSize));
        }

        return Math.max(1, Math.ceil((metadata.num_records || 0) / pageSize));
    }, [dataset, metadata, pageSize, activeFilters, activeSorter]);

    // Compute the correct filters with is_in_dataset based on isArchived prop
    // This ensures the filter is always correct based on the current tab
    // The key prop on DatasetTable ensures React re-renders with new props when switching tabs
    const effectiveFilters = useMemo(() => {
        const filtersWithArchive = { ...currentFilters };
        filtersWithArchive.is_in_dataset = isArchived ? false : true;
        return filtersWithArchive;
    }, [currentFilters, isArchived]);

    // Sync filter state and fetch data when component mounts or when isArchived/isBenchmark changes
    // destroyInactiveTabPane ensures component remounts when tab becomes active
    useEffect(() => {
        if (!currentUsecase) return;

        // Always set the correct filter based on isArchived prop
        const filtersWithArchive = { ...currentFilters };
        filtersWithArchive.is_in_dataset = isArchived ? false : true;

        // Always update filter state
        setCurrentFilters(filtersWithArchive);

        // Always fetch data with the correct filter
        handleTableChange(1, pageSize, isBenchmark, filtersWithArchive, activeSorter);
        setCurrentPage(1);
    }, [isArchived, isBenchmark, currentUsecase?._id]);

    // Ensure current page is valid
    useEffect(() => {
        if (currentPage > actualTotalPages && actualTotalPages > 0) {
            setCurrentPage(1);
            // Reset to first page if current page is invalid
            handleTableChange(1, pageSize, isBenchmark, effectiveFilters, activeSorter);
        }
    }, [actualTotalPages, currentPage, pageSize, isBenchmark, effectiveFilters, activeSorter, handleTableChange]);

    // Track loading transition to stop showing spinner after first load completes
    useEffect(() => {
        if (isLoading && !hasSeenLoading) {
            setHasSeenLoading(true);
        }
        if (hasSeenLoading && !isLoading && !hasLoadedOnce) {
            setHasLoadedOnce(true);
        }
    }, [isLoading, hasSeenLoading, hasLoadedOnce]);

    // Show loading for initial load or during bulk operations
    const effectiveTableLoading = (isLoading && !hasLoadedOnce) || isBulkOperationInProgress;

    // Customize empty text: show "Data is loading" only when a source is configured
    // This prevents flickering between "Data is loading" and the table's default "No data"
    // when the usecase has no datasets/benchmarks configured.
    const shouldShowLoadingEmptyText = (() => {
        const hasConfiguredSource = isBenchmark
            ? (currentUsecase?.benchmarks?.length || 0) > 0
            : (currentUsecase?.datasets?.length || 0) > 0;

        // If there is no configured dataset/benchmark, never show the loading message
        if (!hasConfiguredSource) return false;

        // With a configured source, show loading while fetching
        if (isLoading) return true;

        if (typeof metadata?.num_records === 'number') {
            return metadata.num_records === 0;
        }
        // Metadata not ready yet but source configured → show loading text
        return true;
    })();
    // Show Export whenever there is data available, regardless of loading state
    const showExportButton = (
        (typeof metadata?.num_records === 'number' && metadata.num_records > 0) ||
        (Array.isArray(dataset) && dataset.length > 0)
    );


    // Handle page change
    const onPageChange = (page) => {
        // Validate the page number
        const validPage = Math.min(page, actualTotalPages);
        setCurrentPage(validPage);
        handleTableChange(validPage, pageSize, isBenchmark, effectiveFilters, activeSorter);
    };

    useEffect(() => () => {
        tablePopoverIdsRef.current.clear();
        onTablePopoverChange?.(false);
    }, [onTablePopoverChange]);

    // Fetch display names when component mounts, when currentUsecase changes, or when labels are updated
    useEffect(() => {
        if (currentUsecase) {
            fetchDisplayNames();
        }
    }, [currentUsecase, labelsUpdated]);

    // Reset the display names refreshed flag after animation completes
    useEffect(() => {
        let timer;
        if (displayNamesRefreshed) {
            timer = setTimeout(() => {
                setDisplayNamesRefreshed(false);
            }, 1000); // Duration should match the CSS animation
        }
        return () => {
            if (timer) clearTimeout(timer);
        };
    }, [displayNamesRefreshed]);

    // Function to fetch display names
    const fetchDisplayNames = () => {
        if (currentUsecase?.datasets.length > 0) {
            data_from_server_post(
                "get-display-names",
                currentUsecase._id,
                (response) => {
                    setDisplayNames(response || {});
                    // Trigger refresh animation if labels were updated
                    if (labelsUpdated > 0) {
                        setDisplayNamesRefreshed(true);
                    }
                }
            );
        }
    };

    useEffect(() => {
        if (dataset && dataset.length > 0) {
            // Debug logging for dataset changes when filters are active
            if (activeFilters > 0) {
                console.log(`Dataset after filtering: ${dataset.length} records`);
                if (dataset.length === 0) {
                    console.log("Warning: Empty dataset after filtering!");
                }
            }
        } else if (dataset && dataset.length === 0 && activeFilters > 0) {
            console.log("Empty dataset received with active filters");
        }
    }, [dataset, activeFilters]);

    // Handle search
    const handleSearch = useCallback((query) => {
        setSearchQuery(query);
        // Clear Select All state when searching
        setIsSelectAllActive(false);
        setExcludedIds([]);

        // Add search query to filters
        const searchFilters = { ...currentFilters };
        if (query.trim()) {
            searchFilters.request_example = query.trim();
        } else {
            delete searchFilters.request_example;
        }

        // Ensure is_in_dataset filter is set for archived vs non-archived tabs
        searchFilters.is_in_dataset = isArchived ? false : true;

        // Update filters and trigger data refresh
        setCurrentFilters(searchFilters);
        handleTableChange(1, pageSize, isBenchmark, searchFilters, activeSorter);
    }, [currentFilters, handleTableChange, pageSize, isBenchmark, activeSorter, isArchived]);



    const fetchValidationCounts = useCallback((onSettled) => {
        if (!currentUsecase) return;

        setIsValidationCountsLoading(true);
        const dataset = isBenchmark ? currentUsecase?.benchmarks?.[0] : currentUsecase?.datasets?.[0];
        if (!dataset) {
            setIsValidationCountsLoading(false);
            onSettled?.();
            return;
        }
        data_from_server_post(
            "get-label-counts",
            {
                llm_usecase_id: currentUsecase?._id,
                is_benchmark: isBenchmark
            },
            (response) => {
                if (response?.success) {
                    setValidationProgress(response.label_counts || {});
                    setEntityCounts(response.entity_counts || {});
                    setRelationCounts(response.relation_counts || {});
                    setRecordCounts(response.record_counts || null);
                    setRankScoreInitialized(!!response.rank_score_initialized);
                    setUnlabeledCount(response.unlabeled_count?.total || 0);
                    setNoneSelectedCount(response.none_selected_count?.total || 0);
                }
                setIsValidationCountsLoading(false);
                setHasLoadedValidationCountsOnce(true);
                onSettled?.();
            },
            () => {
                setIsValidationCountsLoading(false);
                setHasLoadedValidationCountsOnce(true);
                onSettled?.();
            }
        );
    }, [currentUsecase, isBenchmark]);

    const handleRefreshCategories = useCallback((removedEntity) => {
        if (typeof removedEntity === 'string' && removedEntity) {
            setEntityCounts((prev) => {
                if (!prev || !(removedEntity in prev)) return prev;
                const next = { ...prev };
                delete next[removedEntity];
                return next;
            });
        }
        refreshCategories?.(removedEntity);
    }, [refreshCategories]);

    // Fetch label counts for the currently active table (datasets vs benchmarks).
    useEffect(() => {
        fetchValidationCounts();
    }, [fetchValidationCounts]);

    useEffect(() => {
        onValidationProgressChange?.(validationProgress);
    }, [validationProgress, onValidationProgressChange]);

    // When AI label generation for this usecase finishes, re-fetch records and
    // counts so rows stop showing "Generating..." without a manual page refresh.
    const generationStatus = generationProgress?.[currentUsecase?._id]?.status ?? null;
    useEffect(() => {
        if (!currentUsecase) return;
        const prevStatus = lastGenerationStatusRef.current;
        lastGenerationStatusRef.current = generationStatus;
        const isTerminal = generationStatus === 'completed' || generationStatus === 'failed';
        // Only refetch on a transition into a terminal status (not on every tick).
        if (!isTerminal || prevStatus === generationStatus) return;
        handleTableChange(currentPage, pageSize, isBenchmark, effectiveFilters, activeSorter);
        fetchValidationCounts?.();
        refreshRecordCounts?.();
        onDataOverviewRefresh?.();
    }, [
        generationStatus,
        currentUsecase,
        currentPage,
        pageSize,
        isBenchmark,
        effectiveFilters,
        activeSorter,
        handleTableChange,
        fetchValidationCounts,
        refreshRecordCounts,
        onDataOverviewRefresh,
    ]);

    // Add modal state for full text view
    const [showFullTextModal, setShowFullTextModal] = useState(false);
    const [selectedRecordId, setSelectedRecordId] = useState(null);
    const [fetchedRecord, setFetchedRecord] = useState(null); // For records not in current dataset

    useEffect(() => {
        onNestedOverlayChange?.(isReplaceLabelModalVisible || showFullTextModal);
    }, [
        isReplaceLabelModalVisible,
        showFullTextModal,
        onNestedOverlayChange,
    ]);

    // Derive selectedRecord from dataset using selectedRecordId - always gets latest version
    // If not found in dataset, use fetchedRecord
    const selectedRecord = selectedRecordId
        ? (dataset?.find(r => r._id === selectedRecordId) || fetchedRecord || null)
        : null;

    const applyUpdatedRecord = useCallback((record, recordIsBenchmark) => {
        updateRecord(record, recordIsBenchmark ?? isBenchmark);
        setFetchedRecord((prev) => (prev && record?._id === prev._id ? record : prev));
    }, [updateRecord, isBenchmark]);

    // Fetch a single record by ID when it's not in the current dataset
    const fetchRecordById = useCallback(async (recordId) => {
        if (!currentUsecase || !datasetName) return null;

        try {
            // Fetch the record using dataset-records endpoint with a filter for the specific ID
            const result = await new Promise((resolve, reject) => {
                data_from_server_post(
                    "dataset-records",
                    {
                        client_id: currentUsecase.client_id,
                        llm_usecase_id: currentUsecase._id,
                        first_index: 0,
                        num_records: 1,
                        is_benchmark: isBenchmark,
                        filters: { _id: recordId }, // Filter by the specific record ID
                        also_null_responses: true,
                    },
                    (response) => {
                        if (response?.records && response.records.length > 0) {
                            resolve(response.records[0]);
                        } else {
                            resolve(null);
                        }
                    },
                    (error) => {
                        console.error("Failed to fetch record by ID:", error);
                        resolve(null); // Resolve with null instead of rejecting to avoid unhandled promise rejection
                    }
                );
            });
            return result;
        } catch (error) {
            console.error("Error fetching record by ID:", error);
            return null;
        }
    }, [currentUsecase, datasetName, isBenchmark]);

    // Check URL on initial mount and when dataset changes
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const recordId = urlParams.get('recordId');

        if (recordId) {
            // Always set selectedRecordId if it's in the URL
            setSelectedRecordId(recordId);

            if (dataset && dataset.length > 0) {
                // Find the record with the matching ID
                const record = dataset.find(r => r._id === recordId);
                if (record) {
                    // Record found in current dataset, clear fetched record and open modal
                    setFetchedRecord(null);
                    setShowFullTextModal(true);
                } else if (!fetchedRecord || fetchedRecord._id !== recordId) {
                    // Record not found in current dataset and we don't have it fetched yet, fetch it
                    fetchRecordById(recordId).then((fetched) => {
                        if (fetched) {
                            setFetchedRecord(fetched);
                            setShowFullTextModal(true);
                        }
                    });
                } else if (fetchedRecord && fetchedRecord._id === recordId) {
                    // We already have the fetched record, just open the modal
                    setShowFullTextModal(true);
                }
            } else {
                // Dataset not loaded yet, fetch the record if we don't have it
                if (!fetchedRecord || fetchedRecord._id !== recordId) {
                    fetchRecordById(recordId).then((fetched) => {
                        if (fetched) {
                            setFetchedRecord(fetched);
                            // Modal will open when selectedRecord becomes available
                        }
                    });
                }
            }
        }
    }, [dataset, fetchRecordById]);

    // Open modal when selectedRecord becomes available (e.g., after dataset loads)
    useEffect(() => {
        if (selectedRecord && selectedRecordId) {
            // Check if recordId is in URL to ensure we should open the modal
            const urlParams = new URLSearchParams(window.location.search);
            const recordId = urlParams.get('recordId');
            if (recordId === selectedRecordId && !showFullTextModal) {
                setShowFullTextModal(true);
            }
        }
    }, [selectedRecord, selectedRecordId, showFullTextModal]);

    // Listen for URL changes (e.g., when user navigates back/forward)
    useEffect(() => {
        const handleUrlChange = () => {
            const urlParams = new URLSearchParams(window.location.search);
            const recordId = urlParams.get('recordId');

            if (!recordId) {
                // No recordId in URL, close modal
                setShowFullTextModal(false);
                setSelectedRecordId(null);
            }
        };

        window.addEventListener('popstate', handleUrlChange);
        return () => window.removeEventListener('popstate', handleUrlChange);
    }, []);

    // Handle modal close and URL cleanup
    const handleModalClose = () => {
        setShowFullTextModal(false);
        setSelectedRecordId(null);
        setFetchedRecord(null); // Clear fetched record when closing modal

        // Remove recordId from URL
        const urlParams = new URLSearchParams(window.location.search);
        urlParams.delete('recordId');
        const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
        window.history.replaceState({}, '', newUrl);
    };
    // Handle selection change for individual checkboxes
    const handleCheckboxChange = useCallback((recordId, checked) => {
        if (isSelectAllActive) {
            // When Select All is active, manage excludedIds
            if (checked) {
                // User checked a record - remove it from excludedIds
                setExcludedIds(prev => prev.filter(id => id !== recordId));
            } else {
                // User unchecked a record - add it to excludedIds
                setExcludedIds(prev => [...prev, recordId]);
            }
        } else {
            // Normal selection mode - manage selectedRowKeys
            if (checked) {
                setSelectedRowKeys(prev => [...prev, recordId]);
            } else {
                setSelectedRowKeys(prev => prev.filter(id => id !== recordId));
            }
        }
    }, [isSelectAllActive]);

    const handleToggleAllOnPage = useCallback((selectAll) => {
        const pageIds = (dataset || []).map(r => r._id).filter(Boolean);
        if (isSelectAllActive) {
            if (selectAll) {
                setExcludedIds(prev => prev.filter(id => !pageIds.includes(id)));
            } else {
                setExcludedIds(prev => [...new Set([...prev, ...pageIds])]);
            }
        } else {
            if (selectAll) {
                setSelectedRowKeys(prev => [...new Set([...prev, ...pageIds])]);
            } else {
                setSelectedRowKeys(prev => prev.filter(id => !pageIds.includes(id)));
            }
        }
    }, [dataset, isSelectAllActive]);

    // Handle select all - activate Select All mode
    const handleSelectAll = useCallback(() => {
        if (!currentUsecase) return;

        // Get total count of filtered records
        const totalCount = metadata?.filteredRecords || metadata?.num_records || 0;

        if (totalCount === 0) {
            sparkToast.info('No records to select');
            return;
        }

        // Activate Select All mode - all records are selected except excluded ones
        setIsSelectAllActive(true);
        setExcludedIds([]);
        setSelectedRowKeys([]); // Clear explicit selection when using Select All mode
    }, [currentUsecase, metadata]);

    // Handle deselect all
    const handleDeselectAll = useCallback(() => {
        setIsSelectAllActive(false);
        setExcludedIds([]);
        setSelectedRowKeys([]);
    }, []);

    // Check if all filtered records are selected (either via Select All or explicit selection)
    const areAllFilteredRecordsSelected = useMemo(() => {
        if (isSelectAllActive) {
            return true; // Select All is active, so all records are considered selected
        }
        const totalFiltered = metadata?.filteredRecords || metadata?.num_records || 0;
        return selectedRowKeys.length > 0 && selectedRowKeys.length >= totalFiltered;
    }, [selectedRowKeys.length, metadata, isSelectAllActive]);

    // Handle bulk archive
    const handleBulkArchive = useCallback(() => {
        if (selectedRowKeys.length === 0 && !isSelectAllActive) return;

        setIsBulkOperationInProgress(true);

        // Always use bulk-operations endpoint
        const formattedFilters = formatFiltersForAPI(currentFilters);
        data_from_server_post(
            "bulk-operations",
            {
                client_id: currentUsecase.client_id,
                llm_usecase_id: currentUsecase._id,
                is_benchmark: isBenchmark,
                operation_type: "archive",
                filters: Object.keys(formattedFilters).length > 0 ? formattedFilters : undefined,
                also_null_responses: false,
                also_null_request_examples: false,
                is_select_all: isSelectAllActive,
                excluded_ids: isSelectAllActive ? excludedIds : undefined,
                included_ids: !isSelectAllActive ? selectedRowKeys : undefined,
            },
            (result) => {
                if (result?.success) {
                    sparkToast.success(`Successfully archived ${result.updated_count} record${result.updated_count > 1 ? 's' : ''}`);
                    setIsSelectAllActive(false);
                    setExcludedIds([]);
                    setSelectedRowKeys([]);
                    setIsBulkOperationInProgress(false);
                    handleTableChange(currentPage, pageSize, isBenchmark, effectiveFilters, activeSorter);
                    // Refresh label counts after archiving
                    if (fetchValidationCounts) {
                        fetchValidationCounts();
                    }
                    // Refresh counts to update badge numbers
                    if (refreshRecordCounts) {
                        refreshRecordCounts();
                    }
                    onDataOverviewRefresh?.();
                } else {
                    sparkToast.error(result?.error || 'Failed to archive records');
                    setIsBulkOperationInProgress(false);
                }
            },
            (error) => {
                sparkToast.error('Failed to archive records');
                setIsBulkOperationInProgress(false);
            }
        );
    }, [selectedRowKeys, isSelectAllActive, excludedIds, isBenchmark, currentUsecase, currentPage, pageSize, handleTableChange, currentFilters, activeSorter, fetchValidationCounts, formatFiltersForAPI, refreshRecordCounts, onDataOverviewRefresh]);

    // Handle bulk unarchive
    const handleBulkUnarchive = useCallback(() => {
        if (selectedRowKeys.length === 0 && !isSelectAllActive) return;

        setIsBulkOperationInProgress(true);

        // Always use bulk-operations endpoint
        const formattedFilters = formatFiltersForAPI(currentFilters);
        data_from_server_post(
            "bulk-operations",
            {
                client_id: currentUsecase.client_id,
                llm_usecase_id: currentUsecase._id,
                is_benchmark: isBenchmark,
                operation_type: "unarchive",
                filters: Object.keys(formattedFilters).length > 0 ? formattedFilters : undefined,
                also_null_responses: false,
                also_null_request_examples: false,
                is_select_all: isSelectAllActive,
                excluded_ids: isSelectAllActive ? excludedIds : undefined,
                included_ids: !isSelectAllActive ? selectedRowKeys : undefined,
            },
            (result) => {
                if (result?.success) {
                    sparkToast.success(`Successfully unarchived ${result.updated_count} record${result.updated_count > 1 ? 's' : ''}`);
                    setIsSelectAllActive(false);
                    setExcludedIds([]);
                    setSelectedRowKeys([]);
                    setIsBulkOperationInProgress(false);
                    handleTableChange(currentPage, pageSize, isBenchmark, effectiveFilters, activeSorter);
                    // Refresh label counts after unarchiving
                    if (fetchValidationCounts) {
                        fetchValidationCounts();
                    }
                    // Refresh counts to update badge numbers
                    if (refreshRecordCounts) {
                        refreshRecordCounts();
                    }
                    onDataOverviewRefresh?.();
                } else {
                    sparkToast.error(result?.error || 'Failed to unarchive records');
                    setIsBulkOperationInProgress(false);
                }
            },
            (error) => {
                sparkToast.error('Failed to unarchive records');
                setIsBulkOperationInProgress(false);
            }
        );
    }, [selectedRowKeys, isSelectAllActive, excludedIds, isBenchmark, currentUsecase, currentPage, pageSize, handleTableChange, currentFilters, activeSorter, fetchValidationCounts, formatFiltersForAPI, refreshRecordCounts, onDataOverviewRefresh]);

    // Handle bulk review
    const handleBulkReview = useCallback(() => {
        if (selectedRowKeys.length === 0 && !isSelectAllActive) return;

        setIsBulkOperationInProgress(true);

        // Always use bulk-operations endpoint
        const formattedFilters = formatFiltersForAPI(currentFilters);
        data_from_server_post(
            "bulk-operations",
            {
                client_id: currentUsecase.client_id,
                llm_usecase_id: currentUsecase._id,
                is_benchmark: isBenchmark,
                operation_type: "review",
                filters: Object.keys(formattedFilters).length > 0 ? formattedFilters : undefined,
                also_null_responses: false,
                also_null_request_examples: false,
                is_select_all: isSelectAllActive,
                excluded_ids: isSelectAllActive ? excludedIds : undefined,
                included_ids: !isSelectAllActive ? selectedRowKeys : undefined,
            },
            (result) => {
                if (result?.success) {
                    sparkToast.success(`Successfully reviewed ${result.updated_count} record${result.updated_count > 1 ? 's' : ''}`);
                    setIsSelectAllActive(false);
                    setExcludedIds([]);
                    setSelectedRowKeys([]);
                    setIsBulkOperationInProgress(false);
                    handleTableChange(currentPage, pageSize, isBenchmark, effectiveFilters, activeSorter);
                    fetchValidationCounts(() => {
                        if (columnPreset === 'improve-modal') {
                            setFocusIncompleteToken((token) => token + 1);
                        }
                    });
                } else {
                    sparkToast.error(result?.error || 'Failed to review records');
                    setIsBulkOperationInProgress(false);
                }
            },
            (error) => {
                sparkToast.error('Failed to review records');
                setIsBulkOperationInProgress(false);
            }
        );
    }, [selectedRowKeys, isSelectAllActive, excludedIds, isBenchmark, currentUsecase, currentPage, pageSize, handleTableChange, currentFilters, activeSorter, fetchValidationCounts, formatFiltersForAPI, columnPreset]);

    // Handle bulk unreview
    const handleBulkUnreview = useCallback(() => {
        if (selectedRowKeys.length === 0 && !isSelectAllActive) return;

        setIsBulkOperationInProgress(true);

        // Always use bulk-operations endpoint
        const formattedFilters = formatFiltersForAPI(currentFilters);
        data_from_server_post(
            "bulk-operations",
            {
                client_id: currentUsecase.client_id,
                llm_usecase_id: currentUsecase._id,
                is_benchmark: isBenchmark,
                operation_type: "unreview",
                filters: Object.keys(formattedFilters).length > 0 ? formattedFilters : undefined,
                also_null_responses: false,
                also_null_request_examples: false,
                is_select_all: isSelectAllActive,
                excluded_ids: isSelectAllActive ? excludedIds : undefined,
                included_ids: !isSelectAllActive ? selectedRowKeys : undefined,
            },
            (result) => {
                if (result?.success) {
                    sparkToast.success(`Successfully unreviewed ${result.updated_count} record${result.updated_count > 1 ? 's' : ''}`);
                    setIsSelectAllActive(false);
                    setExcludedIds([]);
                    setSelectedRowKeys([]);
                    setIsBulkOperationInProgress(false);
                    handleTableChange(currentPage, pageSize, isBenchmark, effectiveFilters, activeSorter);
                    if (fetchValidationCounts) {
                        fetchValidationCounts();
                    }
                } else {
                    sparkToast.error(result?.error || 'Failed to unreview records');
                    setIsBulkOperationInProgress(false);
                }
            },
            (error) => {
                sparkToast.error('Failed to unreview records');
                setIsBulkOperationInProgress(false);
            }
        );
    }, [selectedRowKeys, isSelectAllActive, excludedIds, isBenchmark, currentUsecase, currentPage, pageSize, handleTableChange, currentFilters, activeSorter, fetchValidationCounts, formatFiltersForAPI]);

    // Handle bulk replace label - open modal
    const handleBulkReplaceLabel = useCallback(() => {
        if (selectedRowKeys.length === 0 && !isSelectAllActive) return;
        setIsReplaceLabelModalVisible(true);
    }, [selectedRowKeys.length, isSelectAllActive]);

    // Handle replace label confirmation
    // Note: "delete row" in the modal sets mapping value to null, which the backend interprets as "remove occurrences".
    // This should apply ONLY to the currently filtered/selected records (not the whole dataset).
    const handleReplaceLabelConfirm = useCallback((payload) => {
        if (selectedRowKeys.length === 0 && !isSelectAllActive) return;
        if (!payload || typeof payload !== 'object') return;

        setIsBulkOperationInProgress(true);
        setIsReplaceLabelModalVisible(false);

        const formattedFilters = formatFiltersForAPI(currentFilters);

        data_from_server_post(
            "bulk-operations",
            {
                client_id: currentUsecase.client_id,
                llm_usecase_id: currentUsecase._id,
                is_benchmark: isBenchmark,
                operation_type: "replace_mappings",
                label_mappings: payload.label_mappings,
                entity_mappings: payload.entity_mappings,
                relation_mappings: payload.relation_mappings,
                // Single-label classification "Other"/rejection fallback label
                // re-pointed/cleared from the modal (gated server-side).
                update_rejection_label: payload.update_rejection_label === true,
                rejection_label_name: payload.rejection_label_name ?? null,
                filters: Object.keys(formattedFilters).length > 0 ? formattedFilters : undefined,
                also_null_responses: false,
                also_null_request_examples: false,
                is_select_all: isSelectAllActive,
                excluded_ids: isSelectAllActive ? excludedIds : undefined,
                included_ids: !isSelectAllActive ? selectedRowKeys : undefined,
            },
            (result) => {
                if (result?.success) {
                    const rejectionUpdated = payload.update_rejection_label === true;
                    const recordsUpdatedCount = result.updated_count || 0;
                    if (recordsUpdatedCount > 0 && rejectionUpdated) {
                        sparkToast.success(
                            `Successfully updated ${recordsUpdatedCount} record${recordsUpdatedCount > 1 ? 's' : ''} and the "Fallback" label`
                        );
                    } else if (rejectionUpdated) {
                        sparkToast.success('"Fallback" label updated successfully');
                    } else {
                        sparkToast.success(
                            `Successfully updated ${recordsUpdatedCount} record${recordsUpdatedCount > 1 ? 's' : ''}`
                        );
                    }
                    // Reflect the re-pointed "Fallback" label so Edit Task / next
                    // upload see the new value immediately (optimistic), then
                    // re-sync from the server so the persisted value is the
                    // source of truth even after a page refresh.
                    if (rejectionUpdated) {
                        if (updateUsecase) {
                            updateUsecase(currentUsecase._id, {
                                rejection_label_name: payload.rejection_label_name ?? null,
                            });
                        }
                        if (refreshUsecases) {
                            refreshUsecases();
                        }
                    }
                    setIsSelectAllActive(false);
                    setExcludedIds([]);
                    setSelectedRowKeys([]);
                    setIsBulkOperationInProgress(false);
                    // Refresh the table data immediately
                    handleTableChange(currentPage, pageSize, isBenchmark, effectiveFilters, activeSorter);
                    if (fetchValidationCounts) {
                        fetchValidationCounts();
                    }
                    if (refreshCategories) {
                        refreshCategories();
                    }
                    onDataOverviewRefresh?.();
                } else {
                    sparkToast.error(result?.error || 'Failed to update values');
                    setIsBulkOperationInProgress(false);
                }
            },
            () => {
                sparkToast.error('Failed to update values');
                setIsBulkOperationInProgress(false);
            }
        );
    }, [selectedRowKeys, isSelectAllActive, excludedIds, isBenchmark, currentUsecase, currentPage, pageSize, handleTableChange, currentFilters, activeSorter, fetchValidationCounts, refreshCategories, formatFiltersForAPI, updateRecord, onDataOverviewRefresh, updateUsecase, refreshUsecases]);

    // Handle bulk "Generate labels" (classification only): snapshot current labels
    // into origin_labels{i}, clear them, and regenerate via AI on the selected records.
    const handleBulkGenerateLabels = useCallback(() => {
        if (selectedRowKeys.length === 0 && !isSelectAllActive) return;

        setIsBulkOperationInProgress(true);

        const formattedFilters = formatFiltersForAPI(currentFilters);
        data_from_server_post(
            "bulk-operations",
            {
                client_id: currentUsecase.client_id,
                llm_usecase_id: currentUsecase._id,
                is_benchmark: isBenchmark,
                operation_type: "generate_labels",
                filters: Object.keys(formattedFilters).length > 0 ? formattedFilters : undefined,
                also_null_responses: false,
                also_null_request_examples: false,
                is_select_all: isSelectAllActive,
                excluded_ids: isSelectAllActive ? excludedIds : undefined,
                included_ids: !isSelectAllActive ? selectedRowKeys : undefined,
            },
            (result) => {
                if (result?.success) {
                    sparkToast.success(
                        `Generating labels for ${result.updated_count} record${result.updated_count > 1 ? 's' : ''}…`
                    );
                    setIsSelectAllActive(false);
                    setExcludedIds([]);
                    setSelectedRowKeys([]);
                    setIsBulkOperationInProgress(false);
                    handleTableChange(currentPage, pageSize, isBenchmark, effectiveFilters, activeSorter);
                    if (fetchValidationCounts) {
                        fetchValidationCounts();
                    }
                    if (refreshRecordCounts) {
                        refreshRecordCounts();
                    }
                    onDataOverviewRefresh?.();
                } else {
                    sparkToast.error(result?.error || 'Failed to start label generation');
                    setIsBulkOperationInProgress(false);
                }
            },
            () => {
                sparkToast.error('Failed to start label generation');
                setIsBulkOperationInProgress(false);
            }
        );
    }, [selectedRowKeys, isSelectAllActive, excludedIds, isBenchmark, currentUsecase, currentPage, pageSize, handleTableChange, currentFilters, activeSorter, fetchValidationCounts, refreshRecordCounts, formatFiltersForAPI, onDataOverviewRefresh]);

    // Apply Labels / Reviewed / Sample Origin / Data Creation / dataset_card / Tools Called column filters (server-side via currentFilters).
    const handleColumnFiltersChange = useCallback((partial) => {
        const next = { ...currentFilters };
        Object.entries(partial || {}).forEach(([key, value]) => {
            // Empty ranked-queue ids are a valid empty table (View AND Filter:).
            if (key === 'record_ids' && Array.isArray(value)) {
                next[key] = value;
                return;
            }
            if (value == null || (Array.isArray(value) && value.length === 0) || value === '') {
                delete next[key];
            } else {
                next[key] = value;
            }
        });
        next.is_in_dataset = isArchived ? false : true;
        setCurrentFilters(next);
        setCurrentPage(1);
        handleTableChange(1, pageSize, isBenchmark, next, activeSorter);
    }, [currentFilters, isArchived, setCurrentFilters, handleTableChange, pageSize, isBenchmark, activeSorter]);

    const handleImprovementModeFilterChange = useCallback((label, kind) => {
        handleColumnFiltersChange(improvementModeFilterToTableFilters(label, kind));
    }, [handleColumnFiltersChange]);

    const handleViewFilterChange = useCallback((label, recordIds) => {
        if (!label) {
            handleColumnFiltersChange({ record_ids: null, view_label: null });
            return;
        }
        handleColumnFiltersChange({ record_ids: recordIds || [], view_label: label });
    }, [handleColumnFiltersChange]);

    // Get table columns
    const columns = getDatasetColumns(
        extraParamsTitles, // Use dynamic extraParamsTitles instead of static ones
        handleCheckboxChange,
        isBenchmark ? currentUsecase?.benchmarks[0] : currentUsecase?.datasets[0],
        isBenchmark,
        currentUsecase?.task_type,
        applyUpdatedRecord,  // Pass refresh function to columns
        metadata,      // Pass metadata to columns
        currentPage,
        pageSize,
        currentUsecase?._id,
        displayNames,  // Pass display names to columns
        allLabelOptions,
        validationProgress,
        fetchValidationCounts,  // Pass fetchValidationCounts function
        handleRefreshCategories,
        currentUsecase?.client_id,
        relationshipTypes,
        refreshRelationshipTypes,
        {
            setShowFullTextModal,
            setSelectedRecord: (record) => setSelectedRecordId(record?._id || null),
            currentUsecase,
            datasetNames: isBenchmark ? (currentUsecase?.benchmarks || []) : (currentUsecase?.datasets || []),
            datasetCardFilterValues,
            toolCallFilterValues,
            onDataOverviewRefresh,
            crossReferenceFields,
            unlabeledCount,
            noneSelectedCount,
            entityCounts,
            relationCounts,
            onColumnFiltersChange: handleColumnFiltersChange,
            onTablePopoverChange: handleTablePopoverChange,
            // Refresh live counts when the Labels filter panel opens, so the
            // Unlabeled / None Selected buckets and per-label totals are
            // correct at the moment of use (no mutation-path wiring needed).
            onLabelsPanelOpen: fetchValidationCounts,
        },
        currentUsecase?.is_multilabel || false, // Pass is_multilabel from usecase
        currentFilters, // Pass current filters to maintain filter state
        () => {
            // Refresh the current page data after a mutation from a row action (e.g. archive/unarchive).
            handleTableChange(currentPage, pageSize, isBenchmark, effectiveFilters, activeSorter);
            if (refreshRecordCounts) {
                refreshRecordCounts();
            }
        },
        selectedRowKeys,
        isSelectAllActive,
        excludedIds,
        dataset || [],
        handleToggleAllOnPage,
    );

    const visibleColumns = columnPreset === 'improve-modal'
        ? filterColumnsForImproveModal(columns)
        : columns;

    return (
        <div className="dataset-table-container">
            <div className="dataset-table-header">
                {/* Generative tasks render the totals widget instead of the
                    per-label review cards (Figma 3968:27495). */}
                {!isBenchmark && !isArchived && <ImprovementMode
                    allLabelOptions={allLabelOptions}
                    currentUsecase={currentUsecase}
                    isBenchmark={isBenchmark}
                    fetchValidationCounts={fetchValidationCounts}
                    isLoading={(isValidationCountsLoading && !hasLoadedValidationCountsOnce) || isBulkOperationInProgress}
                    validationProgress={validationProgress}
                    hasLoadedValidationCounts={hasLoadedValidationCountsOnce}
                    onRetrainRequest={onRetrainRequest}
                    displayNames={displayNames}
                    entityCounts={entityCounts}
                    relationCounts={relationCounts}
                    recordCounts={recordCounts}
                    rankScoreInitialized={rankScoreInitialized}
                    currentFilters={currentFilters}
                    {...restImprovementModeProps}
                    onFilterLabelChange={handleImprovementModeFilterChange}
                    onViewFilterChange={handleViewFilterChange}
                    focusIncompleteToken={parentFocusIncompleteToken + focusIncompleteToken}
                />}
                {title && (
                    <div className="dataset-table-title" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <div style={{ fontWeight: "bold", fontSize: "18px", marginBottom: 10 }}>{title}</div>
                    </div>
                )}


                {description && (
                    <div className="dataset-table-description">
                        {description}
                    </div>
                )}

            </div>

            {/* Conditional rendering: Show BulkActionsBar when records are selected, otherwise show Search and Column Manager */}
            {(selectedRowKeys.length > 0 || isSelectAllActive) ? (
                <BulkActionsBar
                    selectedCount={isSelectAllActive
                        ? (metadata?.filteredRecords || metadata?.num_records || 0) - excludedIds.length
                        : selectedRowKeys.length}
                    totalCount={metadata?.filteredRecords || metadata?.num_records || (dataset?.length || 0)}
                    selectedRecords={(dataset || []).filter(record =>
                        isSelectAllActive
                            ? !excludedIds.includes(record._id)
                            : selectedRowKeys.includes(record._id)
                    )}
                    onSelectAll={handleSelectAll}
                    onDeselectAll={handleDeselectAll}
                    onReview={handleBulkReview}
                    onUnreview={handleBulkUnreview}
                    onReplaceLabel={handleBulkReplaceLabel}
                    onGenerateLabels={handleBulkGenerateLabels}
                    onArchive={handleBulkArchive}
                    onUnarchive={handleBulkUnarchive}
                    isBenchmark={isBenchmark}
                    isArchived={isArchived}
                    disabled={effectiveTableLoading || isBulkOperationInProgress}
                    taskType={currentUsecase?.task_type}
                    isMultilabel={currentUsecase?.is_multilabel || false}
                    isSelectAll={isSelectAllActive}
                />
            ) : (
                <div className="spark-dataset-toolbar">
                    <RequestExampleSearchInput
                        onSearch={handleSearch}
                        placeholder="Search examples"
                        value={currentFilters.request_example || ''}
                    />
                    <div className="spark-dataset-toolbar-actions">
                        {showExportButton && (
                            <div style={isLoading ? { pointerEvents: 'none' } : undefined}>
                                <ExportButton
                                    currentUsecase={currentUsecase}
                                    isBenchmark={isBenchmark}
                                    isArchived={isArchived}
                                    currentFilters={currentFilters}
                                    extraParamsTitles={extraParamsTitles}
                                    role={role}
                                />
                            </div>
                        )}
                        <div style={isLoading ? { pointerEvents: 'none' } : undefined}>
                            <ExtraParamsColumnManager
                                availableExtraParamsKeys={availableExtraParamsKeys || []}
                                currentExtraParamsTitles={extraParamsTitles}
                                onExtraParamsTitlesChange={(titles) => {
                                    setExtraParamsTitles(titles);
                                }}
                                taskType={currentUsecase?.task_type}
                                onOpenChange={(open) => handleTablePopoverChange('edit-columns', open)}
                            />
                        </div>
                    </div>
                </div>
            )}
            <DataTable
                columns={visibleColumns}
                dataSource={Array.isArray(dataset) ? dataset : []}
                loading={effectiveTableLoading}
                rowKey="_id"
                disableHoverBackground
                pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: (() => {
                        const metaTotal = (metadata && typeof metadata.filteredRecords === 'number') ? metadata.filteredRecords : 0;
                        const dataLen = Array.isArray(dataset) ? dataset.length : 0;
                        return Math.max(metaTotal, dataLen);
                    })(),
                    onChange: (page) => {
                        setCurrentPage(page);
                        handleTableChange(page, pageSize, isBenchmark, effectiveFilters, activeSorter);
                    },
                    pageSizeOptions: Array.from(new Set([10, 25, 50, 100, pageSize])).sort((a, b) => a - b),
                    onPageSizeChange: (size) => {
                        setPageSize(size);
                        setCurrentPage(1);
                        handleTableChange(1, size, isBenchmark, effectiveFilters, activeSorter);
                    },
                }}
                emptyText={shouldShowLoadingEmptyText ? 'Data is loading…' : 'No data'}
            />
            {/* Render the FullTextModal at the root */}
            <FullTextModal
                visible={showFullTextModal}
                onClose={handleModalClose}
                record={selectedRecord}
                entityOptions={(allLabelOptions?.entity_names || []).map(l => ({ value: l, label: l, color: '#1890ff' }))}
                onChange={(record, newEntities, newRelations, activity_type, activity_comment) => {
                    const datasetName = resolveDatasetNameForRecord(record, currentUsecase, isBenchmark)
                    record.response.entities = newEntities
                    record.response.relations = newRelations
                    updateRecordResponse(record, record.response, datasetName, isBenchmark, applyUpdatedRecord, fetchValidationCounts, currentUsecase?._id, activity_type, activity_comment, currentUsecase?.task_type, onDataOverviewRefresh)
                }}
                datasetName={datasetName}
                refreshCategories={handleRefreshCategories}
                client_id={currentUsecase?.client_id}
                relationshipTypes={relationshipTypes}
                refreshRelationshipTypes={refreshRelationshipTypes}
                updateRecord={applyUpdatedRecord}
                fetchValidationCounts={fetchValidationCounts}
                llm_usecase_id={currentUsecase?._id}
                isBenchmark={isBenchmark}
                taskType={currentUsecase?.task_type}
                onRefreshRecords={() => {
                    // Refresh the current page data after entity rename
                    handleTableChange(currentPage, pageSize, isBenchmark, effectiveFilters, activeSorter);
                }}
                onDataOverviewRefresh={onDataOverviewRefresh}
            />
            {/* Replace Label Modal */}
            <ReplaceLabelModal
                visible={isReplaceLabelModalVisible}
                onCancel={() => setIsReplaceLabelModalVisible(false)}
                onConfirm={handleReplaceLabelConfirm}
                allLabelOptions={allLabelOptions}
                taskCatalog={{
                    label_names: currentUsecase?.manually_added_labels || [],
                    entity_names: currentUsecase?.manually_added_entities || [],
                    relation_names: currentUsecase?.manually_added_relations || [],
                }}
                taskType={currentUsecase?.task_type}
                isMultilabel={currentUsecase?.is_multilabel === true}
                clientId={currentUsecase?.client_id}
                llmUsecaseId={currentUsecase?._id}
                isBenchmark={isBenchmark}
                filters={formatFiltersForAPI(currentFilters)}
                isSelectAll={isSelectAllActive}
                excludedIds={excludedIds}
                includedIds={selectedRowKeys}
                loading={isBulkOperationInProgress}
                existingRejectionLabelName={currentUsecase?.rejection_label_name || null}
                confidenceRejectionEnabled={currentUsecase?.confidence_rejection_enabled === true}
                scroll={{ x: 'max-content' }}
            />
        </div>
    );
};